import { createContext, useCallback, useContext, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router';

/**
 * App-owned back-history for the in-app `←` arrows on detail pages.
 *
 * The arrows must return to the *actual previous page* across arbitrarily deep
 * trails (match → stadium → match → team → …), but bounded to the last 10 steps
 * (FIFO — going deeper than 10 forgets the oldest step), and a sidebar/navbar
 * navigation must start a fresh trail so a section switch can't be rewound past.
 *
 * A plain `navigate(-1)` can't honor the 10-step cap or the "clear on section
 * switch" rule, so we track a single `backDepth` counter (0–10): how many
 * in-app steps the arrow may rewind. Movement still uses the real
 * `navigate(-1)` so the arrow stays in sync with the browser's own back button;
 * the counter only gates whether we rewind or fall back to the section list.
 *
 *   - PUSH (a forward <Link>/navigate) → backDepth = min(10, backDepth + 1)
 *   - POP  (browser or in-app back)    → backDepth = max(0, backDepth - 1)
 *   - navigation tagged state.navRoot  → backDepth = 0  (sidebar/navbar)
 *   - REPLACE / initial load           → unchanged (starts at 0)
 *
 * `goBack(fallback)` rewinds when backDepth > 0, else navigates to `fallback`
 * (the page's section list) — covering direct loads/refreshes and an exhausted
 * trail without ever leaving the SPA.
 */

const MAX_DEPTH = 10;

interface NavHistoryContextValue {
  /** Go to the previous in-app page, or `fallback` when there's nothing to rewind. */
  goBack: (fallback: string) => void;
}

const NavHistoryContext = createContext<NavHistoryContextValue | null>(null);

export function NavHistoryProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navType = useNavigationType();
  const navigate = useNavigate();

  // Kept in a ref so depth changes never re-render consuming pages.
  const backDepthRef = useRef(0);
  // Dedupes React StrictMode's double-fired effect (same history entry key).
  const lastKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (lastKeyRef.current === location.key) return;
    lastKeyRef.current = location.key;

    const isNavRoot = !!(location.state as { navRoot?: boolean } | null)?.navRoot;
    if (navType === 'POP') {
      backDepthRef.current = Math.max(0, backDepthRef.current - 1);
    } else if (isNavRoot) {
      backDepthRef.current = 0;
    } else if (navType === 'PUSH') {
      backDepthRef.current = Math.min(MAX_DEPTH, backDepthRef.current + 1);
    }
    // REPLACE: depth unchanged.
  }, [location.key, location.state, navType]);

  const goBack = useCallback(
    (fallback: string) => {
      if (backDepthRef.current > 0) navigate(-1);
      else navigate(fallback);
    },
    [navigate],
  );

  const value = useMemo(() => ({ goBack }), [goBack]);

  return <NavHistoryContext.Provider value={value}>{children}</NavHistoryContext.Provider>;
}

export function useNavHistory(): NavHistoryContextValue {
  const ctx = useContext(NavHistoryContext);
  if (!ctx) throw new Error('useNavHistory must be used within NavHistoryProvider');
  return ctx;
}
